import 'moment/locale/fr';
import { createSelector } from 'reselect';
import { getLocale } from 'actions/language/selectors';
import { getViewsByPageTotal } from 'actions/platformUsageAnalytics/selectors';
import { isInRange } from 'helpers/DatepickerHelper';

/*** Basic Selectors ***/
export const getLimit = ({ platformUsage: { limit } }) => limit;

export const getChartOption = ({ platformUsage: { chartOption } }) =>
  chartOption;

export const getPage = ({ platformUsage: { page } }) => page;

export const getLanguageFilterCode = ({ platformUsage: { languageFilter } }) =>
  languageFilter;

export const getLanguageFilterOptions = ({
  platformUsage: { languageFilterOptions }
}) => languageFilterOptions;

export const getChartOptionDropdownOpen = ({
  platformUsage: { chartOptionDropdownOpen }
}) => chartOptionDropdownOpen;

export const getLimitDropdownOpen = ({
  platformUsage: { limitDropdownOpen }
}) => limitDropdownOpen;

export const getLanguageFilterDropdownOpen = ({
  platformUsage: { languageFilterDropdownOpen }
}) => languageFilterDropdownOpen;

export const getDatepickerDropdownOpen = (state) =>
  state.platformUsage.datepickerDropdownOpen;

export const getStartDate = (state) => state.platformUsage.startDate;

export const getEndDate = (state) => state.platformUsage.endDate;

export const getUtcOffset = (state) => `"${state.platformUsage.utcOffset}"`;

export const getGroupFilter = (state) => state.platformUsage.groupFilter;

/*** Memoized Selectors  ***/
export const getGroupFilterID = createSelector(
  [getGroupFilter],
  (groupFilter) => `"${groupFilter.id}"`
);

export const getCurrentDateRange = createSelector(
  [getStartDate, getEndDate],
  (startDate, endDate) =>
    `${startDate.format('YYYYMMDD')}-${endDate.format('YYYYMMDD')}`
);

export const getSkip = createSelector(
  [getPage, getLimit],
  (limit, page) => page * limit
);

export const getTotalPages = createSelector(
  [getViewsByPageTotal, getLimit],
  (total, limit) => new Array(Math.ceil(total / limit)).fill('x')
);

export const getLanguageFilterLabel = createSelector(
  [getLanguageFilterOptions, getLanguageFilterCode],
  (options, selected) => options.find(({ code }) => code === selected).label
);

export const getLowerLimit = createSelector(
  [getPage],
  (page) => Math.floor(page / 5) * 5
);

export const getUpperLimit = createSelector(
  [getLowerLimit],
  (lowerLimit) => lowerLimit + 5
);

/**
 * @return {String} The start timestamp in ISO-8601 format in quotes, for example: '"2019-11-27T00:00:00-05:00"'
 */
export const getStartTimeRequest = createSelector(
  [getStartDate],
  (startDate) => {
    const startOfDay = startDate.clone();
    return `"${startOfDay
      .hours(0)
      .minutes(0)
      .seconds(0)
      .format()}"`;
  }
);

/**
 * @return {String} The end timestamp in ISO-8601 format in quotes, for example: '"2019-11-27T23:59:59-05:00"'
 */
export const getEndTimeRequest = createSelector([getEndDate], (endDate) => {
  const endOfDay = endDate.clone();
  return `"${endOfDay
    .hours(23)
    .minutes(59)
    .seconds(59)
    .format()}"`;
});

const dateFormat = 'YYYY/MM/DD';

export const getDatepickerDropdownLabel = createSelector(
  [getStartDate, getEndDate, getLocale],
  (start, end, locale) => {
    const calendarFormat =
      locale === 'fr' ? 'dddd Do MMM YYYY' : 'dddd, MMM Do YYYY';
    start.locale(locale);
    return (
      isInRange(start, end) ||
      (start.isSame(end, 'day') && start.format(calendarFormat)) ||
      `${start.format(dateFormat)} - ${end.format(dateFormat)}`
    );
  }
);



// WEBPACK FOOTER //
// ./src/actions/platformUsage/selectors.js